// ---------------------------------------------------------------------------
// EM-023 Phase 3 — Per-provider Inbox CQRS event emitter (runtime tier)
// ---------------------------------------------------------------------------
// Story EM-023: "Per-provider inbox optimization detects Gmail/Outlook/Yahoo
// rendering/deliverability issues and suggests fixes."
//
// Phase 1 (LANDED) shipped the pure provider classifier at
// `packages/email-marketing/src/engagement/em-023-provider-inbox-classifier.ts`
// with the canonical event constant
// `EM_023_PROVIDER_SIGNAL_DETECTED_EVENT = "email.provider_signal_detected@v1"`.
//
// Phase 5 (LANDED) shipped the closed CQRS vocabulary in
// `packages/db/src/schema/em-023-provider-inbox-event-catalog.ts`:
//   - `email.provider_signal_detected@v1`
//   - provider vocab (gmail / outlook / yahoo / apple_icloud / generic)
//   - 16-entry deliverability-signal vocab
//   - 12-entry fix-recommendation vocab
//   - 4-level risk-severity ladder (low / medium / high / critical)
//
// Phase 3 (this file): pure event-factory function that builds typed,
// narrowable `Em023ProviderInboxEvent` values so the future inbox-diagnostics
// route + classifier-fan-out service + remediation materialiser all
// `import` ONE source of truth instead of hand-rolling event-type strings
// or re-narrowing the provider / signal / recommendation / severity vocabs
// per-file.
//
// Mirrors the AN-001 (#2414) / MKT-001 (#2419) / PB-006 (#2437) / AN-006
// (#2449) / AN-019 / AN-020 (#2463) / EM-022 Phase 3 emitter pattern
// verbatim — pure builders, no I/O, no env access, no DB. The route +
// classifier-fan-out service owns the actual emission to `customer_events`
// + downstream delivery.
//
// What this file IS:
//   - One pure factory function returning a discriminated-union
//     `Em023ProviderInboxEvent` value.
//   - Boundary asserts: non-empty `tenantId` / `draftId` on every event
//     (catalog requires for tenant scoping + draft correlation); closed-vocab
//     narrowing on `provider` / `signals[]` / `recommendations[]` / `severity`
//     via the Phase 5 guards; ISO-8601 `detectedAt`; non-empty `signals[]`
//     (catalog: "Non-empty list of signals the classifier flagged"); bounded
//     `signals[]` and `recommendations[]` length (catalog row-size guard).
//
// What this file is NOT:
//   - It does not write to `customer_events` — that's the route layer.
//   - It does not carry the `event_id` envelope (CQRS emitter owns that).
//   - It does not classify HTML — that's the Phase 1 classifier.
//   - It is opt-in: zero callers wired today. The future inbox-diagnostics
//     route + remediation materialiser pick this up the moment they land
//     — matches AN-020 / EM-022 minimal-blast-radius convention.
//
// CLAUDE.md Governance touched:
//   #6 Atomic events — one fact per event (1 factory, 1 event type).
//   #7 Event catalog — every event type catalogued (Phase 5) before use.
//   #8 MCP-native — a future inbox-diagnostics MCP tool surfacing provider
//      signal hits to operators picks up this factory so observers never
//      see drifted event-type or vocab tokens.
// ---------------------------------------------------------------------------

import {
  EM_023_DELIVERABILITY_SIGNAL_VOCAB,
  EM_023_FIX_RECOMMENDATION_VOCAB,
  EM_023_INBOX_PROVIDER_VOCAB,
  EM_023_RISK_SEVERITY_VOCAB,
  type Em023DeliverabilitySignalVocab,
  type Em023FixRecommendationVocab,
  type Em023InboxProviderVocab,
  type Em023ProviderInboxEvent,
  type Em023ProviderSignalDetectedPayload,
  type Em023RiskSeverityVocab,
} from "./em-023-provider-inbox-event-catalog";

// Catalog comment: deliverability signals + fix recommendations are
// finite closed vocabs (16 + 12). The classifier MUST not emit duplicates
// — the audit MV row-size guard caps both arrays at the catalog cardinality.
const MAX_SIGNALS_PER_EVENT = EM_023_DELIVERABILITY_SIGNAL_VOCAB.length;
const MAX_RECOMMENDATIONS_PER_EVENT = EM_023_FIX_RECOMMENDATION_VOCAB.length;

// ---------------------------------------------------------------------------
// Boundary asserts — keep emitter PII-free / payload-shape-honest
// ---------------------------------------------------------------------------

function assertNonEmpty(value: string, fieldName: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(
      `EM-023 emitter rejected empty ${fieldName}. Field MUST be a non-empty string.`,
    );
  }
  return value;
}

function assertIsoDatetime(value: string, fieldName: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(
      `EM-023 emitter rejected empty ${fieldName}. Must be an ISO-8601 datetime string.`,
    );
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error(
      `EM-023 emitter rejected non-ISO-8601 ${fieldName} ("${value}"). ` +
        `Must be a parseable ISO-8601 datetime string.`,
    );
  }
  return value;
}

function narrowProvider(value: string): Em023InboxProviderVocab {
  if (!(EM_023_INBOX_PROVIDER_VOCAB as readonly string[]).includes(value)) {
    throw new Error(
      `EM-023 emitter rejected unknown provider "${value}". ` +
        `Must be one of the catalogued EM_023_INBOX_PROVIDER_VOCAB tokens.`,
    );
  }
  return value as Em023InboxProviderVocab;
}

function narrowSignal(value: string): Em023DeliverabilitySignalVocab {
  if (!(EM_023_DELIVERABILITY_SIGNAL_VOCAB as readonly string[]).includes(value)) {
    throw new Error(
      `EM-023 emitter rejected unknown deliverability signal "${value}". ` +
        `Must be one of the catalogued EM_023_DELIVERABILITY_SIGNAL_VOCAB tokens.`,
    );
  }
  return value as Em023DeliverabilitySignalVocab;
}

function narrowRecommendation(value: string): Em023FixRecommendationVocab {
  if (!(EM_023_FIX_RECOMMENDATION_VOCAB as readonly string[]).includes(value)) {
    throw new Error(
      `EM-023 emitter rejected unknown fix recommendation "${value}". ` +
        `Must be one of the catalogued EM_023_FIX_RECOMMENDATION_VOCAB tokens.`,
    );
  }
  return value as Em023FixRecommendationVocab;
}

function narrowSeverity(value: string): Em023RiskSeverityVocab {
  if (!(EM_023_RISK_SEVERITY_VOCAB as readonly string[]).includes(value)) {
    throw new Error(
      `EM-023 emitter rejected unknown risk severity "${value}". ` +
        `Must be one of the catalogued EM_023_RISK_SEVERITY_VOCAB tokens.`,
    );
  }
  return value as Em023RiskSeverityVocab;
}

function narrowSignals(
  values: readonly string[],
): readonly Em023DeliverabilitySignalVocab[] {
  if (!Array.isArray(values)) {
    throw new Error(
      `EM-023 emitter rejected non-array signals (${String(values)}). Must be an array.`,
    );
  }
  if (values.length === 0) {
    throw new Error(
      `EM-023 emitter rejected empty signals[]. Catalog requires a non-empty list of signals the classifier flagged.`,
    );
  }
  if (values.length > MAX_SIGNALS_PER_EVENT) {
    throw new Error(
      `EM-023 emitter rejected oversized signals[] (${values.length} entries). ` +
        `Bounded to ${MAX_SIGNALS_PER_EVENT} (catalog cardinality / row-size guard).`,
    );
  }
  const seen = new Set<string>();
  const narrowed: Em023DeliverabilitySignalVocab[] = [];
  for (const raw of values) {
    if (typeof raw !== "string") {
      throw new Error(
        `EM-023 emitter rejected non-string signal entry (${String(raw)}). Must be a string token.`,
      );
    }
    if (seen.has(raw)) {
      throw new Error(
        `EM-023 emitter rejected duplicate signal "${raw}". Each signal token MUST appear at most once per event.`,
      );
    }
    seen.add(raw);
    narrowed.push(narrowSignal(raw));
  }
  return narrowed;
}

function narrowRecommendations(
  values: readonly string[],
): readonly Em023FixRecommendationVocab[] {
  if (!Array.isArray(values)) {
    throw new Error(
      `EM-023 emitter rejected non-array recommendations (${String(values)}). Must be an array.`,
    );
  }
  if (values.length > MAX_RECOMMENDATIONS_PER_EVENT) {
    throw new Error(
      `EM-023 emitter rejected oversized recommendations[] (${values.length} entries). ` +
        `Bounded to ${MAX_RECOMMENDATIONS_PER_EVENT} (catalog cardinality / row-size guard).`,
    );
  }
  const seen = new Set<string>();
  const narrowed: Em023FixRecommendationVocab[] = [];
  for (const raw of values) {
    if (typeof raw !== "string") {
      throw new Error(
        `EM-023 emitter rejected non-string recommendation entry (${String(raw)}). Must be a string token.`,
      );
    }
    if (seen.has(raw)) {
      throw new Error(
        `EM-023 emitter rejected duplicate recommendation "${raw}". Each recommendation token MUST appear at most once per event.`,
      );
    }
    seen.add(raw);
    narrowed.push(narrowRecommendation(raw));
  }
  return narrowed;
}

// ---------------------------------------------------------------------------
// Catalog-narrower re-exports — drive route-tier validators off ONE source
// ---------------------------------------------------------------------------

export function narrowEm023Provider(value: string): Em023InboxProviderVocab {
  return narrowProvider(value);
}

export function narrowEm023Signal(value: string): Em023DeliverabilitySignalVocab {
  return narrowSignal(value);
}

export function narrowEm023Recommendation(
  value: string,
): Em023FixRecommendationVocab {
  return narrowRecommendation(value);
}

export function narrowEm023Severity(value: string): Em023RiskSeverityVocab {
  return narrowSeverity(value);
}

export {
  EM_023_DELIVERABILITY_SIGNAL_VOCAB,
  EM_023_FIX_RECOMMENDATION_VOCAB,
  EM_023_INBOX_PROVIDER_VOCAB,
  EM_023_RISK_SEVERITY_VOCAB,
};

// ---------------------------------------------------------------------------
// Event factory — discriminated-union producer
// ---------------------------------------------------------------------------

export interface BuildEm023ProviderSignalDetectedInput {
  tenantId: string;
  draftId: string;
  provider: Em023InboxProviderVocab;
  /** Non-empty list of classifier-flagged signals. Each MUST narrow to the Phase 5 vocab. */
  signals: readonly Em023DeliverabilitySignalVocab[];
  /** Possibly-empty list of recommended fixes the UI should surface. */
  recommendations: readonly Em023FixRecommendationVocab[];
  severity: Em023RiskSeverityVocab;
  /** ISO-8601 when the classifier ran. */
  detectedAt: string;
}

export function buildEm023ProviderSignalDetectedEvent(
  input: BuildEm023ProviderSignalDetectedInput,
): Em023ProviderInboxEvent {
  const payload: Em023ProviderSignalDetectedPayload = {
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    draftId: assertNonEmpty(input.draftId, "draftId"),
    provider: narrowProvider(input.provider),
    signals: narrowSignals(input.signals),
    recommendations: narrowRecommendations(input.recommendations),
    severity: narrowSeverity(input.severity),
    detectedAt: assertIsoDatetime(input.detectedAt, "detectedAt"),
  };
  return { type: "email.provider_signal_detected@v1", payload };
}
